// EnterPasswordComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";

import {
  useI18N,
  useLogin,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import Loader from "@/components/common/Loader";

import {
  emailAtom,
  emailCheckWelcomeNameAtom,
  onboardingCompletedAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
  passwordAtom,
} from "@/store/atoms";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
  onBack: () => void;
}

const EnterPasswordComponent = ({
  onClose,
  onBack,
  title,
}: EnterPasswordProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [email] = useAtom(emailAtom);
  const [password, setPassword] = useAtom(passwordAtom);
  const [emailCheckWelcomeName] = useAtom(emailCheckWelcomeNameAtom);
  const { setScreenIndex } = useAuthModals();
  const { modalStyle, modalClassName } = useModalHeight();
  const { performLogin, error, result, isLoading } = useLogin();

  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const [, setIsOnboardingCompleted] = useAtom(onboardingCompletedAtom);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handlePasswordContinue = async () => {
    AnalyticsService.trackGAEventHandler("authPopupEnterPasswordSubmit");
    AnalyticsService.trackMx("authPopupEnterPasswordSubmit", {
      EmailAddress: email,
    });

    // console.log("password entered call performLogin", password);
    performLogin({ email: email, password });
  };

  const handleOpenOnboardingSliderModal = () => {
    console.log("in EPC handleOpenOnboardingSliderModal");
    setOnboardingCurrentScreen(0);
    setIsOnboardingModalOpen(true);
    setIsOnboardingCompleted(false);
  };

  useEffect(() => {
    if (result) {
      if (result.status === "error") {
        console.log("Status Error");

        AnalyticsService.trackGAEventHandler(
          "authPopupEnterPasswordErrorSigningIn",
        );
        AnalyticsService.trackMx("authPopupEnterPasswordErrorSigningIn", {});
      } else {
        const userData = result.data;
        if (userData.firstLogin == 1) {
          AnalyticsService.trackGAEventHandler(
            "authPopupEnterPasswordLoggedInNavigatePermission",
          );
          AnalyticsService.trackMx(
            "authPopupEnterPasswordLoggedInNavigatePermission",
            {},
          );
        } else {
          AnalyticsService.trackGAEventHandler(
            "authPopupEnterPasswordLoggedInNavigateHome",
          );
          AnalyticsService.trackMx(
            "authPopupEnterPasswordLoggedInNavigateHome",
            {},
          );
        }

        //set mixpanelService super property
        AnalyticsService.trackGAPagesHandler(
          "authPopupEnterPasswordClickedLoginMixpanelSuperPropertySet",
        );
        AnalyticsService.trackMx(
          "authPopupEnterPasswordClickedLoginMixpanelSuperPropertySet",
          {
            EmailAddress: email,
          },
        );

        AnalyticsService.setUserProfileProps(userData);

        console.log("Login done");

        if (userData.firstLogin == 1) {
          AnalyticsService.trackGAPagesHandler(
            "authPopupFirstLoginOnboardingOpen",
          );
          AnalyticsService.trackMx("authPopupFirstLoginOnboardingOpen", {
            EmailAddress: email,
          });

          //call open onboarding slider
          handleOpenOnboardingSliderModal();
        }

        setScreenIndex(AuthModalScreen.EnterEmail);
        setPassword("");
        onClose();
      }
    }
  }, [result, setScreenIndex, onClose]);

  useEffect(() => {
    if (error) {
      console.log("500 Error", error);
    }
  }, [error]);

  const handleForgotClick = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupEnterPasswordForgottenPassword",
    );
    AnalyticsService.trackMx("authPopupEnterPasswordForgottenPassword", {});

    //Show forgot password
    setScreenIndex(AuthModalScreen.ForgotPassword);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className="relative h-full bg-bg-brand-secondary-default rounded-xl flex flex-col overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={() => {
            onBack();
            AnalyticsService.trackGAEventHandler("authPopupEnterPasswordClose");
            AnalyticsService.trackMx("authPopupEnterPasswordClose", {});
            setPassword("");
          }}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>
      <div className="flex-1 flex-grow px-8 md:px-16 pt-4 flex flex-col">
        <div className="flex justify-center mb-6">
          <img src="/images/Group.svg" alt="" aria-hidden="true" />
        </div>
        <div className="mb-6">
          <h2 className="title-m-bold text-fg-neutral-strong text-center">
            {`Welcome back, ${emailCheckWelcomeName}!`}
          </h2>
        </div>
        <div className="mb-4">
          Enter your password below to start finding accessible places
        </div>
        <hr className="w-full border-border-token-brand-minimal mb-6" />
        <div className="mb-6">
          <FloatingLabelInput
            label="Password"
            placeholder="Password"
            required
            onChange={handlePasswordChange}
            value={password}
            type="Password"
            errorMessage={error?.message}
          />
        </div>
        <div className="flex-1"></div>
        <div className="mb-8 items-center justify-center flex flex-col">
          <WideButton
            type="filled"
            disabled={password.length <= 0}
            onClick={handlePasswordContinue}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={() => {
              handleForgotClick();
              setPassword("");
            }}
            variant="link"
            className="label-s-medium underline mt-6"
          >
            {i18n.get("forgotYourPassword")}
          </StandardButton>
        </div>
      </div>

      {isLoading && <Loader />}
    </section>
  );
};

export default EnterPasswordComponent;
